"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AltiumProjectMetadataKey = void 0;
exports.getAltiumFileType = getAltiumFileType;
exports.validateAltiumFile = validateAltiumFile;
exports.validateAltiumProjectStructureFile = validateAltiumProjectStructureFile;
exports.detectAltiumProjects = detectAltiumProjects;
const project_detection_1 = require("../project-detection");
const Util_1 = require("../Util");
const altiumRecordFile_1 = require("./altiumRecordFile");
const projectFileParser_1 = require("./parsers/project/projectFileParser");
const PCAD_1 = require("../PCAD");
const altiumHarness_1 = require("./parsers/harness/altiumHarness");
const altiumAnnotation_1 = require("./parsers/annotations/altiumAnnotation");
const SchDocParser_1 = require("./parsers/schdoc/parse/SchDocParser");
const AltiumSheetSymbol_1 = require("./parsers/schdoc/helpers/AltiumSheetSymbol");
const altiumFileTypes_1 = require("./altiumFileTypes");
var AltiumProjectMetadataKey;
(function (AltiumProjectMetadataKey) {
    AltiumProjectMetadataKey["FileName"] = "file-name";
    AltiumProjectMetadataKey["FileType"] = "file-type";
    AltiumProjectMetadataKey["FileVersion"] = "file-version";
    AltiumProjectMetadataKey["Project"] = "altium-project";
    AltiumProjectMetadataKey["ProjectStructure"] = "project-structure";
    AltiumProjectMetadataKey["NetlistAsSchematicModel"] = "netlist-schematic-model";
    AltiumProjectMetadataKey["Harness"] = "harness";
    AltiumProjectMetadataKey["Annotation"] = "annotation";
    AltiumProjectMetadataKey["SchDoc"] = "SchDoc";
})(AltiumProjectMetadataKey || (exports.AltiumProjectMetadataKey = AltiumProjectMetadataKey = {}));
function isTopLevelDocument(record) {
    return record.RECORD === 'TopLevelDocument';
}
function isSheetSymbol(record) {
    return record.RECORD === 'SheetSymbol';
}
function isNoMainPathDocument(record) {
    return record.RECORD === 'NoMainPathDocument';
}
function getAltiumFileType(filename) {
    const path = filename.split('/');
    if (path.length < 1) {
        return false;
    }
    const parts = path[path.length - 1].split('.');
    if (parts.length < 1) {
        return false;
    }
    let ext = parts[parts.length - 1].toLowerCase();
    if (parts.length >= 1) {
        ext = '.' + ext;
    }
    switch (ext.toLowerCase().trim()) {
        case altiumFileTypes_1.AltiumFileType.SchDoc.toLowerCase():
            return altiumFileTypes_1.AltiumFileType.SchDoc;
        case altiumFileTypes_1.AltiumFileType.Sch.toLowerCase():
            return altiumFileTypes_1.AltiumFileType.Sch;
        case altiumFileTypes_1.AltiumFileType.PcbDoc.toLowerCase():
            return altiumFileTypes_1.AltiumFileType.PcbDoc;
        case altiumFileTypes_1.AltiumFileType.Project.toLowerCase():
            return altiumFileTypes_1.AltiumFileType.Project;
        case altiumFileTypes_1.AltiumFileType.ProjectStructure.toLowerCase():
            return altiumFileTypes_1.AltiumFileType.ProjectStructure;
        case altiumFileTypes_1.AltiumFileType.ProjectVariants.toLowerCase():
            return altiumFileTypes_1.AltiumFileType.ProjectVariants;
        case altiumFileTypes_1.AltiumFileType.OutJob.toLowerCase():
            return altiumFileTypes_1.AltiumFileType.OutJob;
        case altiumFileTypes_1.AltiumFileType.PcbDrawing.toLowerCase():
            return altiumFileTypes_1.AltiumFileType.PcbDrawing;
        case altiumFileTypes_1.AltiumFileType.Harness.toLowerCase():
            return altiumFileTypes_1.AltiumFileType.Harness;
        case altiumFileTypes_1.AltiumFileType.BomDoc.toLowerCase():
            return altiumFileTypes_1.AltiumFileType.BomDoc;
        case altiumFileTypes_1.AltiumFileType.Stackup.toLowerCase():
            return altiumFileTypes_1.AltiumFileType.Stackup;
        case altiumFileTypes_1.AltiumFileType.Netlist.toLowerCase():
            return altiumFileTypes_1.AltiumFileType.Netlist;
        case altiumFileTypes_1.AltiumFileType.Annotation.toLowerCase():
            return altiumFileTypes_1.AltiumFileType.Annotation;
    }
    return false;
}
function altiumSemanticType(type) {
    switch (type) {
        case altiumFileTypes_1.AltiumFileType.Sch:
        case altiumFileTypes_1.AltiumFileType.SchDoc:
            return 'schematic';
        case altiumFileTypes_1.AltiumFileType.PcbDoc:
            return 'board';
        case altiumFileTypes_1.AltiumFileType.Project:
            return 'project';
        case altiumFileTypes_1.AltiumFileType.BomDoc:
            return 'bom';
        case altiumFileTypes_1.AltiumFileType.Stackup:
            return 'stackup';
        case altiumFileTypes_1.AltiumFileType.Netlist:
            return 'netlist';
        default:
            return 'other';
    }
}
async function validateAltiumFile(filename, fileContents) {
    const type = getAltiumFileType(filename);
    if (!type) {
        return {
            isValid: false,
            errors: [`Invalid Altium file or contents ${filename}`],
        };
    }
    const semanticType = altiumSemanticType(type);
    let validationResult;
    try {
        switch (type) {
            case altiumFileTypes_1.AltiumFileType.Sch:
            case altiumFileTypes_1.AltiumFileType.SchDoc:
                validationResult = await validateAltiumSch(filename, fileContents);
                break;
            case altiumFileTypes_1.AltiumFileType.PcbDoc:
                validationResult = await validateAltiumPcb(filename, fileContents);
                break;
            case altiumFileTypes_1.AltiumFileType.Project:
                validationResult = await validateAltiumProjectFile(filename, fileContents);
                break;
            case altiumFileTypes_1.AltiumFileType.Harness:
                validationResult = await (0, altiumHarness_1.validateAltiumHarnessFile)(filename, fileContents);
                break;
            case altiumFileTypes_1.AltiumFileType.BomDoc:
                validationResult = await validateAltiumBomFile(filename, fileContents);
                break;
            case altiumFileTypes_1.AltiumFileType.ProjectStructure:
                validationResult = await validateAltiumProjectStructureFile(filename, fileContents);
                break;
            case altiumFileTypes_1.AltiumFileType.Stackup:
                validationResult = await validateAltiumStackupFile(filename, fileContents);
                break;
            case altiumFileTypes_1.AltiumFileType.Netlist:
                validationResult = await validateAltiumNetlistFile(filename, fileContents);
                break;
            case altiumFileTypes_1.AltiumFileType.Annotation:
                validationResult = await (0, altiumAnnotation_1.validateAltiumAnnotationFile)(filename, fileContents);
                break;
            default:
                return {
                    isValid: true,
                    data: {
                        semanticType,
                        fileFormat: {
                            cadTool: 'altium',
                            type: type,
                        },
                        referencedFiles: [],
                        metadata: {},
                    },
                };
        }
    }
    catch (e) {
        return {
            isValid: false,
            semanticType,
            errors: [`Invalid Altium contents ${filename}`, e instanceof Error ? e.message : String(e)],
        };
    }
    if (!validationResult) {
        return {
            isValid: false,
            semanticType,
            errors: [`Invalid Altium contents ${filename}`],
        };
    }
    return {
        isValid: true,
        data: {
            semanticType: semanticType,
            fileFormat: {
                cadTool: 'altium',
                type: type,
                version: validationResult.version,
            },
            referencedFiles: validationResult.referencedFiles,
            metadata: validationResult.metadata,
        },
    };
}
async function validateAltiumBinaryPreamble(fileContents) {
    const expected = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1, 0, 0, 0, 0, 0, 0, 0, 0];
    const arrBuff = new Uint8Array(await fileContents.slice(0, expected.length).arrayBuffer());
    for (let i = 0; i < expected.length; i++) {
        if (arrBuff[i] !== expected[i]) {
            return false;
        }
    }
    return true;
}
async function validateAltiumSch(filename, fileContents) {
    if (!filename.toLowerCase().endsWith('.schdoc')) {
        return undefined;
    }
    const parser = await SchDocParser_1.SchDocParser.FromBlob(fileContents, filename);
    const schematic = await parser.parse();
    const referencedFiles = new Set(schematic.getSheetSymbols().map((s) => (0, AltiumSheetSymbol_1.withDefaultSchDocExtension)(s.getFileNameText())));
    return {
        metadata: {
            [AltiumProjectMetadataKey.FileType]: 'Altium SchDoc Schematic',
            [AltiumProjectMetadataKey.FileVersion]: schematic.version,
            [AltiumProjectMetadataKey.SchDoc]: schematic,
        },
        referencedFiles: [...referencedFiles],
        version: schematic.version,
    };
}
async function validateAltiumPcb(filename, fileContents) {
    if (!filename.toLowerCase().endsWith('.pcbdoc')) {
        return undefined;
    }
    if (!(await validateAltiumBinaryPreamble(fileContents))) {
        return undefined;
    }
    const textContents = await (0, Util_1.getBlobTextCleanPartial)(fileContents, 65536);
    const versionMatch = /PCB\s*(\d+\.\d+)\sBinary\sFile/.exec(textContents);
    if (!versionMatch) {
        return undefined;
    }
    return {
        metadata: {
            [AltiumProjectMetadataKey.FileType]: 'Altium PcbDoc',
            [AltiumProjectMetadataKey.FileVersion]: versionMatch[1],
        },
        referencedFiles: [],
        version: versionMatch[1],
    };
}
async function validateAltiumProjectFile(filename, fileContents) {
    if (!filename.toLowerCase().endsWith('.prjpcb')) {
        return undefined;
    }
    const textContents = await (0, Util_1.getBlobTextClean)(fileContents);
    const parsed = (0, projectFileParser_1.parseAltiumProjectFile)(filename.substring(0, Math.max(filename.lastIndexOf('/'), filename.length)), textContents);
    if (!parsed?.Design?.Version) {
        return undefined;
    }
    const referencedFiles = [];
    for (const document of parsed.Document) {
        if (document.DocumentPath) {
            referencedFiles.push(document.DocumentPath.replace(/\\/g, '/'));
        }
    }
    return {
        metadata: {
            [AltiumProjectMetadataKey.FileType]: altiumFileTypes_1.AltiumFileType.Project,
            [AltiumProjectMetadataKey.FileVersion]: parsed.Design.Version,
            [AltiumProjectMetadataKey.Project]: parsed,
        },
        referencedFiles,
        version: parsed.Design.Version,
    };
}
async function validateAltiumBomFile(filename, fileContents) {
    if (!filename.toLowerCase().endsWith('.bomdoc')) {
        return undefined;
    }
    const textContents = await (0, Util_1.getBlobTextClean)(fileContents);
    const parsed = (0, altiumRecordFile_1.parseAltiumRecordFile)(textContents, undefined, true);
    if (!parsed || parsed.length === 0) {
        return undefined;
    }
    if (!parsed.some((record) => record.RECORD === 'BOM')) {
        return undefined;
    }
    return {
        referencedFiles: [],
        metadata: {
            [AltiumProjectMetadataKey.FileType]: 'Altium BOM',
        },
    };
}
async function validateAltiumProjectStructureFile(filename, fileContents) {
    if (!filename.toLowerCase().endsWith('.prjpcbstructure')) {
        return undefined;
    }
    const referencedFiles = new Set();
    const textContents = await (0, Util_1.getBlobTextClean)(fileContents);
    const parsed = (0, altiumRecordFile_1.parseAltiumRecordFile)(textContents, undefined, true);
    if (!parsed || parsed.length === 0) {
        return undefined;
    }
    let foundTld = false;
    const topLevelDocuments = [];
    const sheetSymbols = [];
    const noMainPathDocuments = [];
    const otherRecords = [];
    for (const record of parsed) {
        if (isTopLevelDocument(record)) {
            foundTld = true;
            topLevelDocuments.push(record);
            if (record.FILENAME) {
                referencedFiles.add(record.FILENAME);
            }
        }
        else if (isSheetSymbol(record)) {
            sheetSymbols.push(record);
            if (record.FILENAME) {
                referencedFiles.add(record.FILENAME);
            }
            if (record.SOURCEDOCUMENT) {
                referencedFiles.add(record.SOURCEDOCUMENT);
            }
        }
        else if (isNoMainPathDocument(record)) {
            noMainPathDocuments.push(record);
            if (record.FILENAME) {
                referencedFiles.add(record.FILENAME);
            }
            if (record.SOURCEDOCUMENT) {
                referencedFiles.add(record.SOURCEDOCUMENT);
            }
        }
        else {
            otherRecords.push(record);
        }
    }
    if (!foundTld) {
        return undefined;
    }
    const statistics = {
        topLevelDocumentCount: topLevelDocuments.length,
        sheetSymbolCount: sheetSymbols.length,
        noMainPathDocumentCount: noMainPathDocuments.length,
        otherRecordCount: otherRecords.length,
        totalReferencedFiles: referencedFiles.size,
    };
    const projectStructureData = {
        [AltiumProjectMetadataKey.FileName]: filename,
        [AltiumProjectMetadataKey.FileType]: altiumFileTypes_1.AltiumFileType.ProjectStructure,
        totalRecords: parsed.length,
        topLevelDocuments,
        sheetSymbols,
        noMainPathDocuments,
        statistics,
    };
    if (otherRecords.length > 0) {
        projectStructureData.otherRecords = otherRecords;
    }
    const metadata = {
        [AltiumProjectMetadataKey.FileType]: altiumFileTypes_1.AltiumFileType.ProjectStructure,
        [AltiumProjectMetadataKey.ProjectStructure]: projectStructureData,
    };
    return {
        metadata,
        referencedFiles: Array.from(referencedFiles),
    };
}
async function validateAltiumStackupFile(filename, fileContents) {
    if (!filename.toLowerCase().endsWith('.stackup')) {
        return undefined;
    }
    const textContents = await (0, Util_1.getBlobTextClean)(fileContents);
    const parsed = (0, altiumRecordFile_1.parseAltiumRecordFile)(textContents, undefined, false, altiumRecordFile_1.AltiumRecordStackupType);
    if (!parsed || parsed.length === 0) {
        return undefined;
    }
    const stackupRecord = parsed.find((record) => record.RECORD === 'STACKUP');
    if (!stackupRecord) {
        return undefined;
    }
    const version = stackupRecord.STACKUPVERSION.toString();
    return {
        referencedFiles: [],
        metadata: {
            [AltiumProjectMetadataKey.FileType]: 'Altium Stackup',
        },
        version,
    };
}
async function validateAltiumNetlistFile(filename, fileContents) {
    if (!filename.toLowerCase().endsWith('.net')) {
        return undefined;
    }
    const textContents = await (0, Util_1.getBlobTextCleanAnsi)(fileContents);
    const result = (0, PCAD_1.validatePcadFile)(filename, textContents);
    if (!result.isValid) {
        return undefined;
    }
    try {
        const parsed = (0, PCAD_1.parsePcadNetlist)(textContents);
        return {
            referencedFiles: [],
            metadata: {
                [AltiumProjectMetadataKey.FileType]: 'Altium Netlist',
                [AltiumProjectMetadataKey.NetlistAsSchematicModel]: parsed.schematic,
            },
            version: '1.0',
            warnings: parsed.warnings,
        };
    }
    catch (e) {
        return undefined;
    }
}
function detectAltiumProjects(files, fileDetails) {
    return (0, project_detection_1.detectAnchoredProjects)({
        tool: 'altium',
        toolDisplayName: 'Altium',
        files,
        fileDetails,
        primaryAnchorMatch: (0, project_detection_1.endsWithExt)(altiumFileTypes_1.AltiumFileType.Project),
        primaryAnchorLabel: altiumFileTypes_1.AltiumFileType.Project,
        secondaryAnchorMatch: (0, project_detection_1.endsWithAnyExt)([altiumFileTypes_1.AltiumFileType.ProjectStructure, altiumFileTypes_1.AltiumFileType.ProjectVariants]),
        secondaryAnchorLabel: `${altiumFileTypes_1.AltiumFileType.ProjectStructure} or ${altiumFileTypes_1.AltiumFileType.ProjectVariants}`,
    });
}
//# sourceMappingURL=altiumValidation.js.map